import { useEffect, useMemo, useState } from 'react';
import {
  evaluateShipping,
  type ShippingEvaluateResult,
  type CouponLineInput,
  type ValidateCouponResult,
} from '../lib/coupons';
import type { Customer } from '../lib/customerAuth';
import { customerDisplayName } from '../lib/customerAuth';
import InlineLogin from './InlineLogin';
import InlineRegister from './InlineRegister';

interface CartItem {
  sku: string;
  title: string;
  price: number;
  brand?: string;
  size?: string;
  photoUrl?: string;
  siteName: string;
  siteId: number;
}

type ShippingMethod = 'pickup' | 'canadapost' | 'ups';

interface ShippingOption {
  id: ShippingMethod;
  label: string;
  description: string;
  price: number | null;
  available: boolean;
}

const SHIPPING_OPTIONS: ShippingOption[] = [
  {
    id: 'pickup',
    label: 'In-Store Pickup',
    description: 'Pick up at our Victoria location \u2014 free',
    price: 0,
    available: true,
  },
  {
    id: 'canadapost',
    label: 'Canada Post',
    description: 'Standard shipping across Canada',
    price: null,
    available: false,
  },
  {
    id: 'ups',
    label: 'UPS',
    description: 'Express shipping (Canada & US)',
    price: null,
    available: false,
  },
];

interface ShippingStepProps {
  items: CartItem[];
  shippingMethod: ShippingMethod;
  onShippingMethodChange: (method: ShippingMethod) => void;
  customerContact: { name: string; email: string; phone: string };
  onContactChange: (contact: { name: string; email: string; phone: string }) => void;
  couponApplied: ValidateCouponResult | null;
  onBack: () => void;
  onProceed: () => void;
  allAvailable: boolean;
  checking: boolean;
  shippingEval: ShippingEvaluateResult | null;
  onShippingEval: (result: ShippingEvaluateResult | null) => void;
  customer: Customer | null;
  onLogin: (email: string, password: string) => Promise<{
    success: boolean;
    error?: string;
    needsVerification?: boolean;
  }>;
  onRegister: (data: {
    email: string;
    password: string;
    firstName?: string;
    lastName?: string;
  }) => Promise<{ success: boolean; error?: string }>;
  onLogout: () => void;
}

export default function ShippingStep({
  items,
  shippingMethod,
  onShippingMethodChange,
  customerContact,
  onContactChange,
  couponApplied,
  onBack,
  onProceed,
  allAvailable,
  checking,
  shippingEval,
  onShippingEval,
  customer,
  onLogin,
  onRegister,
  onLogout,
}: ShippingStepProps) {
  const [contactTouched, setContactTouched] = useState(false);
  const [authView, setAuthView] = useState<'hidden' | 'login' | 'register'>('hidden');

  const couponLines: CouponLineInput[] = useMemo(
    () =>
      items.map((item) => ({
        sku: item.sku,
        site_id: item.siteId,
        price_cents: Math.round(item.price * 100),
      })),
    [items],
  );

  const selectedShipping = SHIPPING_OPTIONS.find((o) => o.id === shippingMethod)!;
  const baseShippingCost = selectedShipping.price ?? 0;

  useEffect(() => {
    if (items.length === 0) {
      onShippingEval(null);
      return;
    }
    let cancelled = false;
    (async () => {
      const result = await evaluateShipping({
        shipping_method: shippingMethod,
        shipping_cents: Math.round(baseShippingCost * 100),
        lines: couponLines,
      });
      if (!cancelled) onShippingEval(result);
    })();
    return () => { cancelled = true; };
  }, [items.length, shippingMethod, baseShippingCost, couponLines, onShippingEval]);

  const merchandiseDiscount =
    couponApplied?.ok && couponApplied.benefit_type === 'item'
      ? (couponApplied.discount_cents || 0) / 100
      : 0;

  const subtotal = items.reduce((sum, item) => sum + item.price, 0);
  const discountedSubtotal = Math.max(0, subtotal - merchandiseDiscount);

  const autoShipDiscount = (shippingEval?.shipping_discount_cents || 0) / 100;
  const couponShipDiscount =
    couponApplied?.ok && couponApplied.benefit_type === 'shipping'
      ? (couponApplied.shipping_discount_cents || 0) / 100
      : 0;
  const shippingDiscount = Math.max(autoShipDiscount, couponShipDiscount);
  const shippingCost = Math.max(0, baseShippingCost - shippingDiscount);
  const estimatedTotal = discountedSubtotal + shippingCost;

  const contactValid =
    customerContact.name.trim().length > 0 &&
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(customerContact.email);

  const handleProceed = () => {
    setContactTouched(true);
    if (!contactValid) return;
    onProceed();
  };

  return (
    <>
      <div className="card">
        <h2>Shipping &amp; Pickup</h2>
        <div className="shipping-options">
          {SHIPPING_OPTIONS.map((option) => (
            <label
              key={option.id}
              className={`shipping-option${shippingMethod === option.id ? ' selected' : ''}${!option.available ? ' disabled' : ''}`}
            >
              <input
                type="radio"
                name="shipping"
                value={option.id}
                checked={shippingMethod === option.id}
                onChange={() => onShippingMethodChange(option.id)}
                disabled={!option.available}
              />
              <div className="shipping-option-content">
                <div className="shipping-option-label">
                  {option.label}
                  {!option.available && (
                    <span className="shipping-coming-soon">Coming Soon</span>
                  )}
                </div>
                <div className="shipping-option-desc">{option.description}</div>
              </div>
              <div className="shipping-option-price">
                {option.price === null
                  ? '\u2014'
                  : option.price === 0
                    ? 'Free'
                    : `$${option.price.toFixed(2)}`}
              </div>
            </label>
          ))}
        </div>
        {shippingEval?.message && (
          <p className="shipping-threshold-hint">{shippingEval.message}</p>
        )}
      </div>

      <div className="card">
        <h2>Contact Information</h2>
        {customer ? (
          <div className="cart-auth-signed-in" style={{ marginBottom: 12 }}>
            <span className="cart-auth-pill">
              Signed in as <strong>{customerDisplayName(customer)}</strong>
            </span>
            <button type="button" className="inline-auth-link-btn" onClick={onLogout}>
              Sign out
            </button>
          </div>
        ) : (
          <>
            <p className="contact-info-hint">
              Have an account? Sign in to autofill your details, or continue as a guest.
            </p>
            <div className="contact-auth-buttons">
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                onClick={() => setAuthView(authView === 'login' ? 'hidden' : 'login')}
              >
                Sign In
              </button>
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                onClick={() => setAuthView(authView === 'register' ? 'hidden' : 'register')}
              >
                Create Account
              </button>
            </div>
            {authView === 'login' && (
              <InlineLogin
                compact
                onLogin={onLogin}
                onSwitchToRegister={() => setAuthView('register')}
                onCancel={() => setAuthView('hidden')}
              />
            )}
            {authView === 'register' && (
              <InlineRegister
                onRegister={onRegister}
                onSwitchToLogin={() => setAuthView('login')}
                onCancel={() => setAuthView('hidden')}
              />
            )}
          </>
        )}
        <p className="contact-info-hint">
          We need this to confirm your order and arrange pickup or delivery.
        </p>
        <div className="contact-form">
          <div className="contact-field">
            <label htmlFor="contact-name">Full Name *</label>
            <input
              id="contact-name"
              type="text"
              placeholder="Jane Smith"
              value={customerContact.name}
              onChange={(e) =>
                onContactChange({ ...customerContact, name: e.target.value })
              }
              className={contactTouched && !customerContact.name.trim() ? 'field-error' : ''}
              required
            />
            {contactTouched && !customerContact.name.trim() && (
              <span className="field-error-msg">Name is required</span>
            )}
          </div>
          <div className="contact-field">
            <label htmlFor="contact-email">Email *</label>
            <input
              id="contact-email"
              type="email"
              placeholder="jane@example.com"
              value={customerContact.email}
              onChange={(e) =>
                onContactChange({ ...customerContact, email: e.target.value })
              }
              className={contactTouched && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(customerContact.email) ? 'field-error' : ''}
              required
            />
            {contactTouched && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(customerContact.email) && (
              <span className="field-error-msg">Valid email is required</span>
            )}
          </div>
          <div className="contact-field">
            <label htmlFor="contact-phone">Phone</label>
            <input
              id="contact-phone"
              type="tel"
              placeholder="250-555-0123"
              value={customerContact.phone}
              onChange={(e) =>
                onContactChange({ ...customerContact, phone: e.target.value })
              }
            />
          </div>
        </div>
      </div>

      <div className="card">
        <h2>Order Summary</h2>
        <div className="cart-summary">
          <span>Subtotal</span>
          <span>${subtotal.toFixed(2)}</span>
        </div>
        {merchandiseDiscount > 0 && (
          <div className="cart-summary discount">
            <span>Coupon {couponApplied?.code}</span>
            <span>&minus;${merchandiseDiscount.toFixed(2)}</span>
          </div>
        )}
        <div className="cart-summary">
          <span>Tax</span>
          <span style={{ color: '#888', fontSize: '0.9em' }}>Calculated at checkout</span>
        </div>
        <div className="cart-summary">
          <span>{shippingMethod === 'pickup' ? 'In-Store Pickup' : 'Shipping'}</span>
          <span>
            {shippingDiscount > 0 && baseShippingCost > 0 ? (
              <>
                <span className="price-original">${baseShippingCost.toFixed(2)}</span>{' '}
                {shippingCost === 0 ? 'Free' : `$${shippingCost.toFixed(2)}`}
              </>
            ) : shippingCost === 0 ? (
              'Free'
            ) : (
              `$${shippingCost.toFixed(2)}`
            )}
          </span>
        </div>
        <div className="cart-summary total">
          <span>Estimated Total</span>
          <span className="total-price">${estimatedTotal.toFixed(2)}+ CAD</span>
        </div>
        <p className="cart-summary-note">Final total including applicable taxes will be calculated at payment.</p>
      </div>

      {!allAvailable && (
        <div className="status-banner error">
          Some items in your cart are no longer available. Please go back and remove them.
        </div>
      )}

      <div className="step-actions">
        <button
          className="btn btn-primary"
          onClick={handleProceed}
          disabled={!allAvailable || checking}
        >
          {checking ? 'Checking availability\u2026' : 'Proceed to Payment'}
        </button>
        <button className="btn btn-secondary" onClick={onBack}>
          Back to Cart
        </button>
      </div>
    </>
  );
}
